export function createNotificationPlayer(soundFile, options = {}) {
    let queue = Promise.resolve();
    let pendingBlockedCount = 0;
    let unlockListenersAttached = false;
    const maxQueue = options.maxQueue || 5;
    const spacingMs = options.spacingMs || 250;
    const volume = options.volume ?? 1;
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const unlockEvents = ['pointerdown', 'mousedown', 'touchstart', 'keydown'];

    const canUseAudio = () => typeof window !== 'undefined' && typeof window.Audio !== 'undefined';

    const detachUnlockListeners = () => {
        if (typeof window === 'undefined' || !unlockListenersAttached) return;
        unlockEvents.forEach((eventName) => window.removeEventListener(eventName, unlockAudio));
        unlockListenersAttached = false;
    };

    const queueBlockedPlay = () => {
        pendingBlockedCount = Math.min(maxQueue, pendingBlockedCount + 1);
        attachUnlockListeners();
    };

    const flushBlockedPlays = () => {
        const count = pendingBlockedCount;
        pendingBlockedCount = 0;
        if (count > 0) play(count);
    };

    const unlockAudio = async () => {
        if (!canUseAudio() || !soundFile) return;
        try {
            const audio = new window.Audio(soundFile);
            audio.muted = true;
            audio.volume = 0;
            audio.preload = 'auto';
            await audio.play();
            audio.pause();
            audio.currentTime = 0;
            detachUnlockListeners();
            flushBlockedPlays();
        } catch (error) {
            attachUnlockListeners();
        }
    };

    function attachUnlockListeners() {
        if (typeof window === 'undefined' || unlockListenersAttached) return;
        unlockEvents.forEach((eventName) => window.addEventListener(eventName, unlockAudio, { passive: true }));
        unlockListenersAttached = true;
    }

    const playOne = async () => {
        if (!canUseAudio() || !soundFile) return;
        const audio = new window.Audio(soundFile);
        audio.volume = volume;
        await audio.play().catch(queueBlockedPlay);
    };

    function play(count = 1) {
        const times = Math.max(1, Math.min(maxQueue, Number(count) || 1));
        for (let index = 0; index < times; index += 1) {
            queue = queue.then(playOne).then(() => wait(spacingMs));
        }
    }

    attachUnlockListeners();
    play.unlock = unlockAudio;
    return play;
}
